'use client'

import ConfirmModal from '@/components/confirm-modal'
import PaginationControls from '@/components/pagination/PaginationControls'
import { TussSelector } from '@/components/tuss/TussSelector'
import {
    applyUnifySuggestion,
    getUnifySuggestions,
    SuggestionGroup,
} from '@/lib/api/catalog/unify-suggestions-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    MdMerge,
    MdRefresh,
    MdVisibilityOff,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'

const DISMISSED_KEY = 'tuss-unify-dismissed'
const PAGE_SIZE = 15

const CONFIDENCE_LABEL: Record<string, string> = {
    high: 'Alta',
    medium: 'Média',
    low: 'Baixa',
}
const CONFIDENCE_BADGE: Record<string, string> = {
    high: 'badge-success',
    medium: 'badge-warning',
    low: 'badge-ghost',
}

const groupKey = (g: SuggestionGroup): string =>
    `${g.companyId}:${g.category}:${g.tussCode}`

interface GroupEdit {
    selectedIds: number[]
    principalId: number
    tussCode: string
}

const loadDismissed = (): Set<string> => {
    try {
        const raw = localStorage.getItem(DISMISSED_KEY)
        return new Set(raw ? (JSON.parse(raw) as string[]) : [])
    } catch {
        return new Set()
    }
}

const saveDismissed = (set: Set<string>): void => {
    try {
        localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]))
    } catch {
        // silencioso
    }
}

export default function TussUnifyPage() {
    const [groups, setGroups] = useState<SuggestionGroup[]>([])
    const [note, setNote] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [edits, setEdits] = useState<Record<string, GroupEdit>>({})
    const [dismissed, setDismissed] = useState<Set<string>>(new Set())

    // Filtros
    const [companyFilter, setCompanyFilter] = useState<string>('')
    const [confidenceFilter, setConfidenceFilter] = useState<string>('')
    const [categoryFilter, setCategoryFilter] = useState<string>('')
    const [hideImaging, setHideImaging] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)

    // Modal de confirmação
    const [confirmGroup, setConfirmGroup] = useState<SuggestionGroup | null>(null)
    const [applying, setApplying] = useState(false)

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300)
        return () => clearTimeout(t)
    }, [searchInput])

    useEffect(() => {
        setDismissed(loadDismissed())
    }, [])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await getUnifySuggestions()
            if (res.success && res.result) {
                const gs = res.result.groups || []
                setGroups(gs)
                setNote(res.result.note)
                // Inicializa o estado editável de cada grupo a partir das sugestões.
                const init: Record<string, GroupEdit> = {}
                for (const g of gs) {
                    init[groupKey(g)] = {
                        selectedIds: g.items.map(i => i.id),
                        principalId: g.principalId,
                        tussCode: g.tussCode,
                    }
                }
                setEdits(init)
            } else {
                toast.error('Não foi possível carregar as sugestões')
            }
        } catch {
            toast.error('Erro ao carregar sugestões de unificação')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const companies = useMemo(
        () => [...new Set(groups.map(g => g.companyId))].sort((a, b) => a - b),
        [groups],
    )

    const filtered = useMemo(() => {
        return groups.filter(g => {
            if (dismissed.has(groupKey(g))) return false
            if (companyFilter && String(g.companyId) !== companyFilter) return false
            if (confidenceFilter && g.confidence !== confidenceFilter) return false
            if (categoryFilter && g.category !== categoryFilter) return false
            if (hideImaging && g.imagingRisk) return false
            if (search) {
                const hay =
                    `${g.tussCode} ${g.tussTerm} ${g.items.map(i => i.name).join(' ')}`.toLowerCase()
                if (!hay.includes(search)) return false
            }
            return true
        })
    }, [groups, dismissed, companyFilter, confidenceFilter, categoryFilter, hideImaging, search])

    useEffect(() => {
        setPage(1)
    }, [companyFilter, confidenceFilter, categoryFilter, hideImaging, search])

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
    const pageGroups = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    const updateEdit = (key: string, patch: Partial<GroupEdit>) => {
        setEdits(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
    }

    const toggleItem = (key: string, id: number) => {
        const cur = edits[key]
        if (!cur) return
        const selected = cur.selectedIds.includes(id)
            ? cur.selectedIds.filter(x => x !== id)
            : [...cur.selectedIds, id]
        // Se desmarcar o principal, escolhe outro selecionado como principal.
        let principalId = cur.principalId
        if (!selected.includes(principalId)) principalId = selected[0] ?? 0
        updateEdit(key, { selectedIds: selected, principalId })
    }

    const dismissGroup = (g: SuggestionGroup) => {
        const next = new Set(dismissed)
        next.add(groupKey(g))
        setDismissed(next)
        saveDismissed(next)
    }

    const handleApply = async () => {
        if (!confirmGroup) return
        const key = groupKey(confirmGroup)
        const edit = edits[key]
        if (!edit) return
        const sourceIds = edit.selectedIds.filter(id => id !== edit.principalId)
        if (!edit.tussCode || !edit.principalId || sourceIds.length === 0) {
            toast.error('Selecione o principal, ao menos um duplicado e o código TUSS')
            return
        }
        setApplying(true)
        try {
            const res = await applyUnifySuggestion({
                companyId: confirmGroup.companyId,
                targetId: edit.principalId,
                sourceIds,
                tussCode: edit.tussCode,
            })
            if (res.success) {
                toast.success(
                    `Unificados ${sourceIds.length} item(ns) em "${res.result?.name ?? edit.tussCode}"`,
                )
                // Remove o grupo aplicado da lista local.
                setGroups(prev => prev.filter(g => groupKey(g) !== key))
                setConfirmGroup(null)
            } else {
                toast.error(res.error || 'Falha ao unificar')
            }
        } catch {
            toast.error('Erro ao aplicar a unificação')
        } finally {
            setApplying(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Cabeçalho */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdMerge className="text-secondary" /> Unificação TUSS
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1 max-w-2xl">
                        Sugestões de itens duplicados do catálogo para fundir em um único,
                        atribuindo o código TUSS oficial. Revise cada grupo antes de aprovar.
                    </p>
                </div>
                <button
                    className="btn btn-ghost btn-sm gap-1"
                    onClick={load}
                    disabled={loading}
                >
                    <MdRefresh className={loading ? 'animate-spin' : ''} /> Recarregar
                </button>
            </div>

            {note && (
                <div className="alert alert-info text-sm mb-4">
                    <span>{note}</span>
                </div>
            )}

            {/* Filtros */}
            <div className="bg-base-200 rounded-box p-4 mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <label className="form-control w-full">
                    <span className="label-text mb-1">Buscar</span>
                    <input
                        type="text"
                        className="input input-bordered input-sm w-full"
                        placeholder="Termo, código ou nome..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                </label>
                <label className="form-control w-full">
                    <span className="label-text mb-1">Empresa</span>
                    <select
                        className="select select-bordered select-sm w-full"
                        value={companyFilter}
                        onChange={e => setCompanyFilter(e.target.value)}
                    >
                        <option value="">Todas</option>
                        {companies.map(c => (
                            <option key={c} value={c}>
                                Empresa #{c}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="form-control w-full">
                    <span className="label-text mb-1">Confiança</span>
                    <select
                        className="select select-bordered select-sm w-full"
                        value={confidenceFilter}
                        onChange={e => setConfidenceFilter(e.target.value)}
                    >
                        <option value="">Todas</option>
                        <option value="high">Alta</option>
                        <option value="medium">Média</option>
                        <option value="low">Baixa</option>
                    </select>
                </label>
                <label className="form-control w-full">
                    <span className="label-text mb-1">Categoria</span>
                    <select
                        className="select select-bordered select-sm w-full"
                        value={categoryFilter}
                        onChange={e => setCategoryFilter(e.target.value)}
                    >
                        <option value="">Todas</option>
                        <option value="EXAM">Exame</option>
                        <option value="PROCEDURE">Procedimento</option>
                        <option value="CONSULTATION">Consulta</option>
                        <option value="TELECONSULTATION">Teleconsulta</option>
                    </select>
                </label>
                <label className="form-control w-full justify-end">
                    <span className="label cursor-pointer gap-2 justify-start">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-sm"
                            checked={hideImaging}
                            onChange={e => setHideImaging(e.target.checked)}
                        />
                        <span className="label-text">Ocultar imagem (risco)</span>
                    </span>
                </label>
            </div>

            {/* Resumo */}
            <div className="flex items-center justify-between mb-3 text-sm text-base-content/70">
                <span>
                    {filtered.length} grupo(s) pendente(s)
                    {filtered.length > 0 &&
                        ` — pág. ${page} de ${totalPages}`}
                </span>
            </div>

            {/* Lista */}
            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-base-content/50">
                    Nenhuma sugestão pendente com os filtros atuais.
                </div>
            ) : (
                <div className="space-y-4">
                    {pageGroups.map(g => {
                        const key = groupKey(g)
                        const edit = edits[key]
                        if (!edit) return null
                        const selectedCount = edit.selectedIds.filter(
                            id => id !== edit.principalId,
                        ).length
                        const canApply =
                            !!edit.tussCode && !!edit.principalId && selectedCount > 0
                        return (
                            <div
                                key={key}
                                className="card bg-base-100 border border-base-300 shadow-sm"
                            >
                                <div className="card-body p-4 gap-3">
                                    {/* Cabeçalho do grupo */}
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="badge badge-neutral font-mono">
                                                    {g.tussCode}
                                                </span>
                                                <span className="font-semibold">
                                                    {g.tussTerm}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                <span className="badge badge-sm badge-outline">
                                                    Empresa #{g.companyId}
                                                </span>
                                                <span className="badge badge-sm badge-outline">
                                                    {g.category}
                                                </span>
                                                <span
                                                    className={`badge badge-sm ${CONFIDENCE_BADGE[g.confidence]}`}
                                                >
                                                    Confiança: {CONFIDENCE_LABEL[g.confidence]}
                                                </span>
                                                {g.imagingRisk && (
                                                    <span className="badge badge-sm badge-error gap-1">
                                                        <MdWarning /> Imagem: revisar
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-ghost btn-xs gap-1"
                                            onClick={() => dismissGroup(g)}
                                            title="Ignorar esta sugestão"
                                        >
                                            <MdVisibilityOff /> Ignorar
                                        </button>
                                    </div>

                                    {g.imagingRisk && (
                                        <div className="alert alert-warning py-2 text-xs">
                                            <MdWarning />
                                            <span>
                                                Itens de imagem (TC/RM/RX/US) podem ter sido
                                                agrupados por engano (regiões do corpo
                                                diferentes). Confira item a item antes de aprovar.
                                            </span>
                                        </div>
                                    )}

                                    {/* Itens */}
                                    <div className="overflow-x-auto">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th className="w-12">Incluir</th>
                                                    <th className="w-20">Principal</th>
                                                    <th>Nome atual</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {g.items.map(item => {
                                                    const included = edit.selectedIds.includes(
                                                        item.id,
                                                    )
                                                    const isPrincipal =
                                                        edit.principalId === item.id
                                                    return (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    className="checkbox checkbox-sm"
                                                                    checked={included}
                                                                    onChange={() =>
                                                                        toggleItem(key, item.id)
                                                                    }
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="radio"
                                                                    name={`principal-${key}`}
                                                                    className="radio radio-sm"
                                                                    checked={isPrincipal}
                                                                    disabled={!included}
                                                                    onChange={() =>
                                                                        updateEdit(key, {
                                                                            principalId: item.id,
                                                                        })
                                                                    }
                                                                />
                                                            </td>
                                                            <td
                                                                className={
                                                                    isPrincipal
                                                                        ? 'font-semibold'
                                                                        : ''
                                                                }
                                                            >
                                                                {item.name}
                                                                <span className="text-xs text-base-content/40 ml-1">
                                                                    #{item.id}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Rodapé: override TUSS + aprovar */}
                                    <div className="flex flex-wrap items-end justify-between gap-3 pt-1">
                                        <div className="w-full md:w-80">
                                            <TussSelector
                                                value={edit.tussCode}
                                                size="sm"
                                                label="Código TUSS (ajustável)"
                                                onChange={code =>
                                                    updateEdit(key, { tussCode: code || '' })
                                                }
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-base-content/60">
                                                {selectedCount} duplicado(s) → principal
                                            </span>
                                            <button
                                                className="btn btn-secondary btn-sm gap-1"
                                                disabled={!canApply}
                                                onClick={() => setConfirmGroup(g)}
                                            >
                                                <MdMerge /> Aprovar unificação
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Paginação */}
            {filtered.length > PAGE_SIZE && (
                <PaginationControls
                    page={page}
                    limit={PAGE_SIZE}
                    total={filtered.length}
                    label="grupo"
                    onPageChange={setPage}
                    className="mt-6"
                />
            )}

            <ConfirmModal
                isOpen={!!confirmGroup}
                onClose={() => setConfirmGroup(null)}
                onConfirm={handleApply}
                loading={applying}
                title="Confirmar unificação"
                message={
                    confirmGroup
                        ? `Os itens selecionados serão fundidos no principal e renomeados para o termo TUSS. Esta ação reaponta todo o histórico e não pode ser desfeita facilmente. Confirmar?`
                        : ''
                }
                confirmButtonText="Unificar"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-secondary"
            />
        </div>
    )
}
